import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FiCheck, FiMic, FiSearch, FiSend, FiZap } from "react-icons/fi";

/** Typogram home: pill strip + detail + animated mock (per-tab motion presets). All product pillars in one scrollable row. */
export const HOME_WORKFLOW_FEATURES = [
  {
    id: "steno",
    label: "Steno",
    title: "Shorthand dictation built for real exams",
    subtitle:
      "SSC, Court, HSSC, Railway & more—listen, transcribe, and review with passages organised by course and chapter.",
    bullets: [
      "Exam-style audio dictation & timing",
      "Volumes, chapters, and passage library",
      "Pitman lessons alongside practice",
    ],
    ctaPath: "/steno",
    mockMint: true,
    mockScene: "dictation",
    mockKind: "checks",
    mockTitle: "Steno hub",
    mockUser: { initials: "ST", name: "Dictation · Vol 2", sub: "80 WPM · English" },
    mockRows: [
      { label: "Exam track", value: "SSC Steno", ok: true },
      { label: "Audio", value: "Ready", ok: true },
      { label: "Passages", value: "Open", ok: true },
      { label: "Certificate", value: "—", ok: false },
    ],
    mockBarLabel: "Track readiness",
    mockBarPct: 78,
    mockBarGradient: "linear-gradient(90deg, #dc2626, #f87171)",
  },
  {
    id: "courses",
    label: "Courses",
    title: "Structured paths for SSC, Railway & more",
    subtitle:
      "Pick a category, follow curated lessons, and practice on passages that match real test formats—not random text.",
    bullets: [
      "SSC, CHSL, RRB, Banking & other categories",
      "Progress saved to your account",
      "Jump between courses anytime",
    ],
    ctaPath: "/courses",
    mockMint: true,
    mockScene: "coursepath",
    mockKind: "checks",
    mockTitle: "Course hub",
    mockUser: { initials: "TG", name: "Typogram learner", sub: "SSC CGL · English typing" },
    mockRows: [
      { label: "Active course", value: "SSC Tier-III", ok: true },
      { label: "Modules done", value: "12/18", ok: true },
      { label: "Practice streak", value: "7 days", ok: true },
      { label: "Certificate", value: "—", ok: false },
    ],
    mockBarLabel: "Path completion",
    mockBarPct: 72,
    mockBarGradient: "linear-gradient(90deg, #22c55e, #16a34a)",
  },
  {
    id: "lesson",
    label: "Lesson",
    title: "Learn in steps, not scattered tabs",
    subtitle:
      "Structured lessons across typing and shorthand—progress saves to your profile so you always pick up where you left off.",
    bullets: [
      "Curated lesson paths & modules",
      "Progress & completion tracking",
      "Jump to practice when you are ready",
    ],
    ctaPath: "/learn",
    mockMint: true,
    mockScene: "lessonsteps",
    mockKind: "stat",
    mockTitle: "Lesson",
    mockStatTitle: "Module progress",
    mockStatSub: "You are on lesson 6 of 12 in this course",
    mockRows: [
      { label: "Lessons done", value: "6/12", ok: true },
      { label: "Time this week", value: "2h 10m", ok: true },
      { label: "Next unlock", value: "Thu", ok: true },
      { label: "Quiz avg", value: "88%", ok: true },
    ],
    mockBarLabel: "Course completion",
    mockBarPct: 52,
    mockBarGradient: "linear-gradient(90deg, #38bdf8, #0ea5e9)",
  },
  {
    id: "practice",
    label: "Practice",
    title: "Typing sessions that feel like test day",
    subtitle:
      "Timed passages, live WPM & accuracy, and a clean typing surface—whether you use a course passage or your own text.",
    bullets: [
      "Custom or course passages",
      "Instant speed & error feedback",
      "Pause, resume, and repeat runs",
    ],
    ctaPath: "/typing-display",
    mockMint: false,
    mockScene: "livetype",
    mockKind: "checks",
    mockTitle: "Session",
    mockUser: { initials: "PR", name: "Practice run", sub: "5 min · standard layout" },
    mockRows: [
      { label: "Timer", value: "Running", ok: true },
      { label: "Words", value: "186", ok: true },
      { label: "Accuracy", value: "97%", ok: true },
      { label: "Layout", value: "US", ok: true },
    ],
    mockBarLabel: "Session progress",
    mockBarPct: 64,
    mockBarGradient: "linear-gradient(90deg, #a855f7, #3b82f6)",
  },
  {
    id: "accuracy",
    label: "Accuracy",
    title: "See mistakes before they become habits",
    subtitle:
      "Word-level comparison highlights slips so you know exactly what to drill next.",
    bullets: [
      "Side-by-side with target text",
      "Error patterns on tough keys",
      "Replay-friendly summaries",
    ],
    ctaPath: "/typing-display",
    mockMint: false,
    mockScene: "diffview",
    mockKind: "stat",
    mockTitle: "Last result",
    mockStatTitle: "Passage alignment",
    mockStatSub: "248 of 255 words matched exactly",
    mockRows: [
      { label: "Accuracy", value: "97.2%", ok: true },
      { label: "Wrong words", value: "5", ok: true },
      { label: "Extra spaces", value: "2", ok: true },
      { label: "Adjusted WPM", value: "68", ok: true },
    ],
    mockBarLabel: "Quality score",
    mockBarPct: 94,
    mockBarGradient: "linear-gradient(90deg, #f59e0b, #d97706)",
  },
  {
    id: "speed",
    label: "Speed",
    title: "Charts that reward consistency",
    subtitle:
      "Rolling averages and bests across sessions so progress is obvious—not buried in one-off scores.",
    bullets: [
      "History across practice runs",
      "Compare to your personal best",
      "Daily challenge tie-ins",
    ],
    ctaPath: "/profile",
    mockMint: false,
    mockScene: "trendchart",
    mockKind: "checks",
    mockTitle: "Trends",
    mockUser: { initials: "TR", name: "Speed analytics", sub: "Last 14 days" },
    mockRows: [
      { label: "Avg WPM", value: "71", ok: true },
      { label: "Peak", value: "84", ok: true },
      { label: "vs last week", value: "+4", ok: true },
      { label: "Consistency", value: "Solid", ok: true },
    ],
    mockBarLabel: "Goal fit",
    mockBarPct: 76,
    mockBarGradient: "linear-gradient(90deg, #c084fc, #3b82f6)",
  },
  {
    id: "races",
    label: "Races",
    title: "Compete without losing the learning loop",
    subtitle:
      "Races, daily challenges, and leaderboards add pressure in a fun way—then you return to focused practice.",
    bullets: [
      "Multiplayer typing races",
      "Daily challenge streaks",
      "Fair matchmaking by skill band",
    ],
    ctaPath: "/race",
    mockMint: true,
    mockScene: "racetrack",
    mockKind: "checks",
    mockTitle: "Lobby",
    mockUser: { initials: "RC", name: "Quick race", sub: "3 opponents · 60s" },
    mockRows: [
      { label: "Queue", value: "Matched", ok: true },
      { label: "Avg rating", value: "Similar", ok: true },
      { label: "Ping", value: "32ms", ok: true },
      { label: "Ready", value: "4/4", ok: true },
    ],
    mockBarLabel: "Readiness",
    mockBarPct: 88,
    mockBarGradient: "linear-gradient(90deg, #ec4899, #db2777)",
  },
  {
    id: "chat",
    label: "Chat",
    title: "Friends & messages without leaving Typogram",
    subtitle:
      "Stay in touch with study buddies, share wins, and coordinate sessions—right next to your practice tools.",
    bullets: [
      "Friends list & chat threads",
      "Online status at a glance",
      "Tied to your Typogram account",
    ],
    ctaPath: "/friends",
    mockMint: true,
    mockScene: "chat",
    mockKind: "checks",
    mockTitle: "Messages",
    mockUser: { initials: "CH", name: "Study circle", sub: "4 friends online" },
    mockRows: [
      { label: "Unread", value: "2", ok: true },
      { label: "Last reply", value: "Just now", ok: true },
      { label: "Mute", value: "Off", ok: true },
      { label: "Sync", value: "Live", ok: true },
    ],
    mockBarLabel: "Inbox health",
    mockBarPct: 82,
    mockBarGradient: "linear-gradient(90deg, #22c55e, #16a34a)",
  },
  {
    id: "community",
    label: "Community",
    title: "Community features beyond solo practice",
    subtitle:
      "Study rooms, groups, challenges, and shared spaces—so typing practice connects to real people, not just metrics.",
    bullets: [
      "Community hub & study rooms",
      "Groups, events, and discussions",
      "Wellness & peer support options",
    ],
    ctaPath: "/community",
    mockMint: true,
    mockScene: "community",
    mockKind: "celebration",
    mockTitle: "Community",
    mockCelebrationTitle: "You are connected",
    mockCelebrationSub: "Joined 3 groups · study room available",
    mockRows: [],
    mockBarLabel: "Engagement",
    mockBarPct: 91,
    mockBarGradient: "linear-gradient(90deg, #ec4899, #be185d)",
  },
  {
    id: "account",
    label: "Account",
    title: "One login for everything on Typogram",
    subtitle:
      "Subscriptions, extensions, and profile settings stay in one place—whether you study on web or mobile.",
    bullets: [
      "Secure sign-in & profile",
      "Manage plans in a few clicks",
      "Support & feedback built in",
    ],
    ctaPath: "/profile",
    mockMint: true,
    mockScene: "account",
    mockKind: "celebration",
    mockTitle: "Account",
    mockCelebrationTitle: "All systems go",
    mockCelebrationSub: "Profile synced · subscription ready",
    mockRows: [],
    mockBarLabel: "Account health",
    mockBarPct: 96,
    mockBarGradient: "linear-gradient(90deg, #22c55e, #15803d)",
  },
  {
    id: "leaderboard",
    label: "Leaderboard",
    title: "See where you stand across Typogram",
    subtitle:
      "Global and seasonal boards reward consistency—climb tiers, compare with friends, and celebrate milestones.",
    bullets: ["Seasonal resets & fair brackets", "Friend comparisons & filters", "Badges tied to rank milestones"],
    ctaPath: "/leaderboard",
    mockMint: false,
    mockScene: "podium",
    mockKind: "checks",
    mockTitle: "Rankings",
    mockUser: { initials: "LB", name: "This season", sub: "Tier Gold · Top 12%" },
    mockRows: [
      { label: "Your rank", value: "#184", ok: true },
      { label: "Region", value: "IN · North", ok: true },
      { label: "Streak bonus", value: "+2%", ok: true },
      { label: "Next tier", value: "142 pts", ok: true },
    ],
    mockBarLabel: "Season progress",
    mockBarPct: 71,
    mockBarGradient: "linear-gradient(90deg, #eab308, #ca8a04)",
  },
  {
    id: "daily",
    label: "Daily",
    title: "One fresh challenge every day",
    subtitle:
      "Short timed prompts keep skills sharp without a full session—streaks and bonus XP when you show up daily.",
    bullets: ["New passage each day", "Streak tracking & reminders", "Lightweight next to full practice"],
    ctaPath: "/daily-challenge",
    mockMint: true,
    mockScene: "dailycard",
    mockKind: "stat",
    mockTitle: "Daily",
    mockStatTitle: "Today’s challenge",
    mockStatSub: "Complete before midnight in your timezone",
    mockRows: [
      { label: "Streak", value: "9 days", ok: true },
      { label: "Best time", value: "1m 02s", ok: true },
      { label: "Bonus XP", value: "Claimed", ok: true },
      { label: "Tomorrow", value: "Unlocked", ok: true },
    ],
    mockBarLabel: "Day progress",
    mockBarPct: 100,
    mockBarGradient: "linear-gradient(90deg, #a855f7, #60a5fa)",
  },
  {
    id: "games",
    label: "Games",
    title: "Typing games that still build skill",
    subtitle:
      "Arcade modes and mini-games reinforce speed and accuracy—fun first, but every round still counts toward growth.",
    bullets: ["Curated arcade titles", "Quick sessions between lessons", "Scores sync to your profile"],
    ctaPath: "/games",
    mockMint: true,
    mockScene: "arcade",
    mockKind: "checks",
    mockTitle: "Games hub",
    mockUser: { initials: "GM", name: "Word Blaster", sub: "High score · this week" },
    mockRows: [
      { label: "Last played", value: "2h ago", ok: true },
      { label: "Unlocked", value: "6/8", ok: true },
      { label: "Weekly rank", value: "#42", ok: true },
      { label: "Rewards", value: "2 pending", ok: true },
    ],
    mockBarLabel: "Arcade progress",
    mockBarPct: 69,
    mockBarGradient: "linear-gradient(90deg, #a855f7, #3b82f6)",
  },
  {
    id: "achievements",
    label: "Badges",
    title: "Achievements that mirror real progress",
    subtitle:
      "Earn badges for streaks, exam prep milestones, and community moments—visible on your profile and shareable.",
    bullets: ["Skill & streak achievements", "Exam-prep milestones", "Profile showcase"],
    ctaPath: "/achievements",
    mockMint: true,
    mockScene: "badgegrid",
    mockKind: "celebration",
    mockTitle: "Achievements",
    mockCelebrationTitle: "New badge unlocked",
    mockCelebrationSub: "7-day practice streak · +50 XP",
    mockRows: [],
    mockBarLabel: "Collection",
    mockBarPct: 58,
    mockBarGradient: "linear-gradient(90deg, #c084fc, #3b82f6)",
  },
  {
    id: "help",
    label: "Help",
    title: "Guides and answers when you need them",
    subtitle:
      "Search the help center, browse FAQs, or reach the team—so you spend less time stuck and more time typing.",
    bullets: ["Searchable help articles", "Account & billing FAQs", "Contact paths from one place"],
    ctaPath: "/help",
    mockMint: false,
    mockScene: "helpsearch",
    mockKind: "checks",
    mockTitle: "Help center",
    mockUser: { initials: "?", name: "How do I reset progress?", sub: "Suggested articles" },
    mockRows: [
      { label: "Articles", value: "142", ok: true },
      { label: "Match", value: "3 results", ok: true },
      { label: "Updated", value: "2026", ok: true },
      { label: "Ticket", value: "Optional", ok: true },
    ],
    mockBarLabel: "Self-serve score",
    mockBarPct: 92,
    mockBarGradient: "linear-gradient(90deg, #64748b, #475569)",
  },
];

const PANEL_PRESETS = [
  {
    initial: { opacity: 0, x: -52, rotateY: -8 },
    animate: { opacity: 1, x: 0, rotateY: 0 },
    exit: { opacity: 0, x: 40, rotateY: 6 },
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
  {
    initial: { opacity: 0, x: 56 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -36 },
    transition: { type: "spring", stiffness: 420, damping: 32, mass: 0.85 },
  },
  {
    initial: { opacity: 0, scale: 0.92, rotateX: 8, filter: "blur(6px)" },
    animate: { opacity: 1, scale: 1, rotateX: 0, filter: "blur(0px)" },
    exit: { opacity: 0, scale: 0.95, rotateX: -6, filter: "blur(4px)" },
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
  {
    initial: { opacity: 0, y: 36 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -28 },
    transition: { type: "spring", stiffness: 280, damping: 22 },
  },
  {
    initial: { opacity: 0, scale: 0.88, rotate: -2 },
    animate: { opacity: 1, scale: 1, rotate: 0 },
    exit: { opacity: 0, scale: 1.04, rotate: 2 },
    transition: { type: "spring", stiffness: 360, damping: 18 },
  },
  {
    initial: { opacity: 0, scale: 0.75 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.9 },
    transition: { duration: 0.42, ease: [0.34, 1.56, 0.64, 1] },
  },
];

const ROW_STAGGER_PRESETS = [
  { staggerChildren: 0.08, delayChildren: 0.12 },
  { staggerChildren: 0.055, delayChildren: 0.06 },
  { staggerChildren: 0.1, delayChildren: 0.15 },
  { staggerChildren: 0.065, delayChildren: 0.09 },
  { staggerChildren: 0.045, delayChildren: 0.05 },
  { staggerChildren: 0.09, delayChildren: 0.18 },
];

function hashString(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(h);
}

function panelPresetForWorkflowId(id) {
  return PANEL_PRESETS[hashString(String(id)) % PANEL_PRESETS.length];
}

function buildRowVariants(tabIndex) {
  const base = [
    {
      hidden: { opacity: 0, x: -22, filter: "blur(4px)" },
      show: { opacity: 1, x: 0, filter: "blur(0px)", transition: { duration: 0.4 } },
    },
    {
      hidden: { opacity: 0, x: 26 },
      show: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 400, damping: 28 } },
    },
    {
      hidden: { opacity: 0, y: 14, scale: 0.96 },
      show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.38 } },
    },
    {
      hidden: { opacity: 0, scale: 0.85, rotate: -3 },
      show: { opacity: 1, scale: 1, rotate: 0, transition: { type: "spring", stiffness: 500, damping: 24 } },
    },
    {
      hidden: { opacity: 0, skewX: -6 },
      show: { opacity: 1, skewX: 0, transition: { duration: 0.36 } },
    },
    {
      hidden: { opacity: 0, clipPath: "inset(0 100% 0 0)" },
      show: { opacity: 1, clipPath: "inset(0 0% 0 0)", transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
    },
  ];
  return base[tabIndex % base.length];
}

function barTransitionForTab(tabIndex) {
  const springs = [
    { type: "spring", stiffness: 90, damping: 18, delay: 0.22, mass: 0.8 },
    { duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.28 },
    { type: "spring", stiffness: 120, damping: 20, delay: 0.18 },
    { duration: 1.05, ease: [0.65, 0, 0.35, 1], delay: 0.32 },
    { type: "spring", stiffness: 200, damping: 26, delay: 0.25 },
    { duration: 0.75, ease: [0.34, 1.3, 0.64, 1], delay: 0.2 },
  ];
  return springs[tabIndex % springs.length];
}

const ShowcaseSection = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  background: radial-gradient(ellipse 150% 100% at 50% 0%, #1c1033 0%, #0f0a18 50%, #050507 100%);
  padding: 3rem 1rem 3.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(circle at 20% 30%, rgba(168, 85, 247, 0.22) 0%, transparent 45%),
      radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.18) 0%, transparent 48%),
      radial-gradient(circle at 50% 100%, rgba(168, 85, 247, 0.08) 0%, transparent 55%);
  }
  @media (min-width: 768px) {
    padding: 3.5rem 1.5rem 4rem;
  }
`;

const ShowcaseInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1280px;
  margin: 0 auto;
`;

const FeatureShowcaseTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  text-align: center;
  color: #fff;
  margin: 0 0 0.75rem;
  letter-spacing: -0.03em;
  @media (min-width: 768px) {
    font-size: 2.35rem;
  }
`;

/** Same gradient as HomeComponent SectionTitle span (“Explore Courses”) */
const ExploreCoursesAccent = styled.span`
  background: linear-gradient(135deg, #a855f7, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const FeatureShowcaseSub = styled.p`
  font-size: 1.05rem;
  text-align: center;
  color: #94a3b8;
  max-width: 42rem;
  margin: 0 auto 2.5rem;
  line-height: 1.65;
  @media (min-width: 768px) {
    font-size: 1.15rem;
  }
`;

const PillStripOuter = styled.div`
  position: relative;
  margin: 0 auto 2.5rem;
  width: 100%;
  max-width: 50%;
  overflow: visible;
  @media (max-width: 640px) {
    max-width: 92%;
  }
`;

const PillStripFade = styled.div`
  position: absolute;
  top: -10px;
  bottom: -10px;
  width: 3.25rem;
  z-index: 2;
  pointer-events: none;
  @media (min-width: 640px) {
    width: 4.75rem;
  }
  @media (min-width: 1024px) {
    width: 5.5rem;
  }
`;

const PillStripFadeLeft = styled(PillStripFade)`
  left: 0;
  background: linear-gradient(
    to right,
    #050507 0%,
    rgba(5, 5, 7, 0.92) 28%,
    rgba(5, 5, 7, 0.35) 72%,
    transparent 100%
  );
`;

const PillStripFadeRight = styled(PillStripFade)`
  right: 0;
  background: linear-gradient(
    to left,
    #050507 0%,
    rgba(5, 5, 7, 0.92) 28%,
    rgba(5, 5, 7, 0.35) 72%,
    transparent 100%
  );
`;

const PillStripScroll = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 0.45rem;
  padding: 0 0.75rem 0.45rem;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PillStripBtn = styled.button`
  flex-shrink: 0;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
  font-size: 0.7rem;
  @media (min-width: 640px) {
    padding: 0.55rem 1.1rem;
    font-size: 0.8rem;
  }
  @media (min-width: 768px) {
    font-size: 0.88rem;
    padding: 0.6rem 1.25rem;
  }
  ${(p) =>
    p.$active
      ? `
    background: linear-gradient(135deg, #a855f7 0%, #3b82f6 100%);
    color: #fff;
    box-shadow: 0 8px 28px rgba(168, 85, 247, 0.38);
    transform: scale(1.05);
  `
      : `
    background: rgba(255, 255, 255, 0.08);
    color: #cbd5e1;
    &:hover {
      background: rgba(255, 255, 255, 0.14);
      color: #f8fafc;
      transform: scale(1.04);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    }
  `}
`;

const FeatureDetailGrid = styled.div`
  display: grid;
  gap: 1.75rem;
  align-items: center;
  @media (min-width: 768px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
    gap: 2.5rem 3.5rem;
  }
`;

const FeatureDetailHeading = styled.h3`
  display: none;
  font-size: 1.65rem;
  font-weight: 800;
  color: #f1f5f9;
  margin: 0 0 0.85rem;
  letter-spacing: -0.02em;
  @media (min-width: 768px) {
    display: block;
  }
`;

const FeatureDetailHeadingMobile = styled.h3`
  font-size: 1.35rem;
  font-weight: 800;
  color: #f1f5f9;
  margin: 0 0 0.65rem;
  @media (min-width: 768px) {
    display: none;
  }
`;

const FeatureDetailLead = styled.p`
  font-size: 0.95rem;
  color: #94a3b8;
  margin: 0 0 1.25rem;
  line-height: 1.65;
  @media (min-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 1.5rem;
  }
`;

const FeatureBulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  @media (min-width: 768px) {
    gap: 0.85rem;
    margin-bottom: 1.75rem;
  }
`;

const FeatureBulletItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.92rem;
  color: #f1f5f9;
  line-height: 1.5;
  @media (min-width: 768px) {
    font-size: 1.05rem;
    gap: 0.85rem;
  }

  &::before {
    content: ${(p) => (p.$step != null ? `"${p.$step}"` : '""')};
    display: ${(p) => (p.$step != null ? 'flex' : 'none')};
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 1.65rem;
    height: 1.65rem;
    border-radius: 8px;
    background: linear-gradient(135deg, #6366f1, #a855f7);
    color: #fff;
    font-size: 0.72rem;
    font-weight: 800;
    margin-top: 0.05rem;
    @media (min-width: 768px) {
      width: 1.85rem;
      height: 1.85rem;
      font-size: 0.78rem;
    }
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.15rem;
    color: #34d399;
    width: 1.1rem;
    height: 1.1rem;
    @media (min-width: 768px) {
      width: 1.35rem;
      height: 1.35rem;
      margin-top: 0.2rem;
    }
  }
`;

const FeatureCtaBtn = styled.button`
  display: inline-block;
  padding: 0.65rem 1.35rem;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #a855f7 0%, #3b82f6 100%);
  color: #fff;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  box-shadow: 0 4px 18px rgba(168, 85, 247, 0.35);
  &:hover {
    filter: brightness(1.05);
    transform: scale(1.03) translateY(-1px);
    box-shadow: 0 8px 28px rgba(59, 130, 246, 0.45);
  }
`;

const MockShell = styled.div`
  background: ${(p) =>
    p.$mint
      ? "linear-gradient(145deg, rgba(16, 185, 129, 0.12) 0%, rgba(13, 13, 26, 0.65) 45%, rgba(5, 5, 7, 0.85) 100%)"
      : "linear-gradient(135deg, rgba(168, 85, 247, 0.14) 0%, rgba(13, 13, 26, 0.55) 50%, rgba(5, 5, 7, 0.75) 100%)"};
  border-radius: 18px;
  padding: 1rem;
  border: 1px solid
    ${(p) => (p.$mint ? "rgba(52, 211, 153, 0.28)" : "rgba(168, 85, 247, 0.28)")};
  box-shadow: ${(p) =>
    p.$mint
      ? "0 12px 40px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.04) inset"
      : "0 12px 40px rgba(0, 0, 0, 0.45), 0 0 48px rgba(168, 85, 247, 0.14)"};
  width: 100%;
  @media (min-width: 768px) {
    padding: 1.35rem;
    min-width: 0;
  }
`;

const MockWindow = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.12);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  font-size: 1.05rem;
  @media (min-width: 768px) {
    font-size: 1.08rem;
  }
`;

const MockChrome = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
`;

const MockDots = styled.div`
  display: flex;
  gap: 0.35rem;
  position: relative;
  z-index: 2;
`;

const MockDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.$c || "#d1d5db"};
`;

const MockChromeTitle = styled.span`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  max-width: 60%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MockBody = styled.div`
  padding: 1rem 1.1rem 1.15rem;
  min-height: 300px;
  @media (min-width: 768px) {
    min-height: 380px;
    padding: 1.25rem 1.35rem 1.45rem;
  }
`;

const MockUserBlock = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
`;

const MockAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #a855f7, #3b82f6);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.02em;
`;

const MockUserText = styled.div`
  min-width: 0;
`;

const MockUserName = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.25;
`;

const MockUserSub = styled.div`
  font-size: 0.68rem;
  color: #9ca3af;
  margin-top: 0.1rem;
`;

const MockDataRow = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.55rem;
  margin-bottom: 0.4rem;
  border-radius: 8px;
  background: ${(p) => (p.$ok ? "#f0fdf4" : "#fff")};
  border: 1px solid ${(p) => (p.$ok ? "#bbf7d0" : "#e5e7eb")};
`;

const MockDataRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
`;

const MockRowIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 0.65rem;
  flex-shrink: 0;
  background: ${(p) => (p.$ok ? "#22c55e" : "#e5e7eb")};
  color: ${(p) => (p.$ok ? "#fff" : "#9ca3af")};
`;

const MockDataLabel = styled.span`
  font-size: 0.74rem;
  font-weight: 600;
  color: #374151;
`;

const MockDataValue = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  flex-shrink: 0;
`;

const MockStatCard = styled.div`
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.75rem;
`;

const MockStatCardTitle = styled.div`
  font-size: 0.88rem;
  font-weight: 800;
  color: #92400e;
  margin-bottom: 0.25rem;
`;

const MockStatCardSub = styled.div`
  font-size: 0.72rem;
  color: #b45309;
  line-height: 1.4;
`;

const MockCelebrationBox = styled(motion.div)`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 0.9rem 1rem;
  margin-bottom: 0.85rem;
`;

const MockCelebrationInner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
`;

const MockCelebrationIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #dcfce7;
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MockCelebrationTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: #14532d;
`;

const MockCelebrationSub = styled.div`
  font-size: 0.68rem;
  color: #15803d;
  margin-top: 0.2rem;
`;

const MockClaimRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.55rem;
  margin-bottom: 0.65rem;
  border-bottom: 1px solid #f3f4f6;
`;

const MockClaimId = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: #111827;
`;

const MockPill = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: ${(p) => p.$bg || "#dcfce7"};
  color: ${(p) => p.$fg || "#166534"};
`;

const MockBarLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.35rem;
`;

const MockBarLabel = styled.span`
  font-size: 0.78rem;
  color: #6b7280;
`;

const MockBarValue = styled.span`
  font-size: 1rem;
  font-weight: 800;
  color: #059669;
`;

const MockBarTrack = styled.div`
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
`;

const SceneRoot = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  min-height: 0;
`;

const SceneRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const SceneCaption = styled.p`
  margin: 0;
  font-size: 0.66rem;
  color: #9ca3af;
  line-height: 1.4;
`;

const SceneTypingSurface = styled.div`
  position: relative;
  background: #0f172a;
  border-radius: 10px;
  padding: 0.85rem 0.9rem 0.95rem;
  min-height: 86px;
  border: 1px solid rgba(148, 163, 184, 0.28);
`;

const SceneTypingBadge = styled(motion.div)`
  position: absolute;
  top: 0.45rem;
  right: 0.45rem;
  font-size: 0.6rem;
  font-weight: 800;
  padding: 0.12rem 0.4rem;
  border-radius: 6px;
  background: rgba(34, 197, 94, 0.18);
  color: #4ade80;
`;

const TypingLine = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.7rem;
  line-height: 1.55;
  color: #e2e8f0;
`;

const TypingCursor = styled.span`
  display: inline-block;
  width: 6px;
  margin-left: 2px;
  height: 1em;
  background: #a855f7;
  vertical-align: text-bottom;
  animation: wf-cursor-blink 1.05s step-end infinite;
  @keyframes wf-cursor-blink {
    50% {
      opacity: 0;
    }
  }
`;

const DiffLine = styled.div`
  font-size: 0.68rem;
  line-height: 1.45;
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  margin-bottom: 0.3rem;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: #1f2937;
`;

const StepCard = styled.div`
  padding: 0.48rem 0.6rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: ${(p) => (p.$active ? "#f0fdf4" : "#fafafa")};
  font-size: 0.7rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
`;

const ChatBubble = styled.div`
  max-width: 88%;
  padding: 0.45rem 0.6rem;
  border-radius: 12px;
  font-size: 0.72rem;
  line-height: 1.35;
  ${(p) =>
    p.$mine
      ? `
    margin-left: auto;
    background: linear-gradient(135deg, #ede9fe, #dbeafe);
    border: 1px solid #c4b5fd;
    color: #1e1b4b;
  `
      : `
    margin-right: auto;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    color: #374151;
  `}
`;

const CommunityCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  border: 1px solid #fce7f3;
  background: #fdf2f8;
  font-size: 0.68rem;
  font-weight: 600;
  color: #831843;
`;

const PodiumRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.25rem 0 0.15rem;
`;

const PodiumBlock = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: ${(p) => p.$w};
  border-radius: 8px 8px 4px 4px;
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$bd};
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
  padding-bottom: 0.35rem;
`;

const SearchFake = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.55rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 0.72rem;
  color: #6b7280;
`;

const WAVE_COUNT = 22;

function WorkflowMockScene({ workflow }) {
  const h = hashString(workflow.id);
  const scene = workflow.mockScene;

  const waveHeights = useMemo(() => {
    const out = [];
    for (let i = 0; i < WAVE_COUNT; i += 1) {
      const v = 18 + ((h + i * 7919) % 74);
      out.push(v);
    }
    return out;
  }, [h]);

  if (!scene) return null;

  switch (scene) {
    case "dictation":
      return (
        <SceneRoot>
          <SceneRow style={{ justifyContent: "flex-start", gap: "0.5rem" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#111827",
              }}
            >
              <FiMic size={15} color="#dc2626" aria-hidden />
              Dictation
            </span>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#ef4444",
                boxShadow: "0 0 0 3px rgba(239,68,68,0.25)",
              }}
              aria-hidden
            />
            <span style={{ fontSize: "0.65rem", color: "#6b7280", fontWeight: 600 }}>Recording</span>
          </SceneRow>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: 3,
              height: 52,
              padding: "0.35rem 0.4rem",
              background: "linear-gradient(180deg,#f8fafc,#fff)",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
            }}
          >
            {waveHeights.map((base, i) => (
              <motion.div
                key={`w-${workflow.id}-${i}`}
                animate={{ height: [base * 0.35, base, base * 0.5, base * 0.85] }}
                transition={{
                  duration: 1.1 + (i % 5) * 0.08,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.04,
                }}
                style={{
                  width: 4,
                  borderRadius: 2,
                  minHeight: 6,
                  background: "linear-gradient(180deg,#dc2626,#f87171)",
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <SceneCaption style={{ margin: 0 }}>Live transcript</SceneCaption>
            <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#374151", fontVariantNumeric: "tabular-nums" }}>
              2:34 / 5:00
            </span>
          </div>
          <TypingLine style={{ color: "#334155", background: "#f8fafc", padding: "0.45rem 0.55rem", borderRadius: 8, border: "1px solid #e2e8f0" }}>
            THE HONOURABLE COURT IS NOW IN SESSION…
            <TypingCursor />
          </TypingLine>
        </SceneRoot>
      );

    case "coursepath":
      return (
        <SceneRoot>
          <SceneCaption>Your active path — modules unlock as you go.</SceneCaption>
          <StepCard $active>
            <span>SSC CGL · English</span>
            <span style={{ color: "#16a34a", fontSize: "0.62rem" }}>● Active</span>
          </StepCard>
          <StepCard>
            <span>Tier-II · Speed block</span>
            <span style={{ color: "#9ca3af", fontSize: "0.62rem" }}>6 / 12</span>
          </StepCard>
          <StepCard>
            <span>Mock exam week</span>
            <span style={{ color: "#9ca3af", fontSize: "0.62rem" }}>Locked</span>
          </StepCard>
        </SceneRoot>
      );

    case "lessonsteps":
      return (
        <SceneRoot>
          <div
            style={{
              display: "flex",
              gap: "0.55rem",
              padding: "0.55rem 0.6rem",
              borderRadius: 10,
              border: "1px solid #e0e7ff",
              background: "linear-gradient(135deg,#eef2ff,#fff)",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: "#4f46e5",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.65rem",
                fontWeight: 800,
              }}
            >
              ▶
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "0.75rem", fontWeight: 800, color: "#1e1b4b" }}>Lesson 6 · Accuracy drills</div>
              <div style={{ fontSize: "0.65rem", color: "#6366f1", marginTop: 2 }}>Video · 12 min · save progress</div>
            </div>
          </div>
          <MockDataRow
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            $ok
            style={{ marginBottom: 0 }}
          >
            <MockDataRowLeft>
              <MockRowIcon $ok>✓</MockRowIcon>
              <MockDataLabel>Lesson quiz</MockDataLabel>
            </MockDataRowLeft>
            <MockDataValue>Passed</MockDataValue>
          </MockDataRow>
          <MockDataRow initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} $ok={false} style={{ marginBottom: 0, background: "#fff" }}>
            <MockDataRowLeft>
              <MockRowIcon $ok={false} />
              <MockDataLabel>Next · Timed drill</MockDataLabel>
            </MockDataRowLeft>
            <MockDataValue>Up next</MockDataValue>
          </MockDataRow>
        </SceneRoot>
      );

    case "livetype":
      return (
        <SceneRoot>
          <SceneTypingSurface>
            <SceneTypingBadge animate={{ opacity: [1, 0.75, 1] }} transition={{ duration: 2.2, repeat: Infinity }}>
              74 WPM · live
            </SceneTypingBadge>
            <TypingLine>
              The quick brown fox jumps over the lazy dog. Score stays{" "}
              <span style={{ color: "#4ade80" }}>green</span> when you hold rhythm.
              <TypingCursor />
            </TypingLine>
          </SceneTypingSurface>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.68rem", color: "#6b7280" }}>
            <span>
              <strong style={{ color: "#111827" }}>Accuracy</strong> 97%
            </span>
            <span>
              <strong style={{ color: "#111827" }}>Timer</strong> 2:41 left
            </span>
          </div>
        </SceneRoot>
      );

    case "diffview":
      return (
        <SceneRoot>
          <SceneCaption>Word-level compare — slips show up instantly.</SceneCaption>
          <DiffLine $bg="#f0fdf4" $border="#bbf7d0">
            <strong style={{ color: "#14532d" }}>Target</strong>
            <br />
            The committee approved the budget without delay.
          </DiffLine>
          <DiffLine $bg="#fef2f2" $border="#fecaca">
            <strong style={{ color: "#991b1b" }}>Your run</strong>
            <br />
            The committee <mark style={{ background: "#fecaca", padding: "0 2px", borderRadius: 3 }}>aproved</mark> the budget
            without delay.
          </DiffLine>
        </SceneRoot>
      );

    case "trendchart": {
      const bars = [42, 55, 48, 62, 71, 68, 74];
      return (
        <SceneRoot>
          <SceneCaption>Last 7 sessions — rolling average climbs with consistency.</SceneCaption>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 4, height: 96, padding: "0.35rem 0.2rem" }}>
            {bars.map((pct, i) => (
              <motion.div
                key={`b-${workflow.id}-${i}`}
                initial={{ height: 0 }}
                animate={{ height: `${pct}%` }}
                transition={{ type: "spring", stiffness: 120, damping: 16, delay: i * 0.05 }}
                style={{
                  flex: 1,
                  maxWidth: 36,
                  borderRadius: 6,
                  background: i === bars.length - 1 ? "linear-gradient(180deg,#a855f7,#3b82f6)" : "linear-gradient(180deg,#c4b5fd,#818cf8)",
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.68rem", color: "#64748b" }}>
            <span>Mon</span>
            <span style={{ fontWeight: 700, color: "#0f172a" }}>Today · peak 84</span>
            <span>Sun</span>
          </div>
        </SceneRoot>
      );
    }

    case "racetrack":
      return (
        <SceneRoot>
          <SceneCaption>Four lanes — first to finish the passage wins.</SceneCaption>
          {[
            { name: "You", color: "#a855f7", w: 0.82 },
            { name: "Riya", color: "#ec4899", w: 0.76 },
            { name: "dev_92", color: "#3b82f6", w: 0.71 },
            { name: "kai", color: "#64748b", w: 0.64 },
          ].map((lane, i) => (
            <div key={lane.name} style={{ marginBottom: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.65rem", marginBottom: 3, color: "#475569" }}>
                <span style={{ fontWeight: 700 }}>{lane.name}</span>
                <span>{Math.round(lane.w * 100)}%</span>
              </div>
              <div style={{ height: 8, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                <motion.div
                  animate={{ width: [`${lane.w * 55}%`, `${lane.w * 100}%`, `${lane.w * 88}%`] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.12 }}
                  style={{ height: "100%", borderRadius: 999, background: lane.color }}
                />
              </div>
            </div>
          ))}
        </SceneRoot>
      );

    case "chat":
      return (
        <SceneRoot>
          <ChatBubble>
            <span style={{ fontSize: "0.6rem", color: "#9ca3af", display: "block", marginBottom: 2 }}>Asha</span>
            Ready for a quick race after this passage?
          </ChatBubble>
          <ChatBubble $mine>
            <span style={{ fontSize: "0.6rem", color: "#6366f1", display: "block", marginBottom: 2 }}>You</span>
            Give me 2 min — finishing a drill.
          </ChatBubble>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.65rem", color: "#9ca3af", marginTop: 4 }}>
            <FiZap size={12} aria-hidden />
            Typing…
          </div>
        </SceneRoot>
      );

    case "community":
      return (
        <SceneRoot>
          <CommunityCard>
            <span>Study room · North</span>
            <motion.span
              animate={{ opacity: [1, 0.4, 1] }}
              transition={{ duration: 1.8, repeat: Infinity }}
              style={{ fontSize: "0.58rem", color: "#db2777", fontWeight: 800 }}
            >
              LIVE
            </motion.span>
          </CommunityCard>
          <CommunityCard style={{ background: "#f0fdf4", borderColor: "#bbf7d0", color: "#14532d" }}>
            <span>Group · SSC 2026</span>
            <span style={{ fontSize: "0.58rem", opacity: 0.85 }}>32 online</span>
          </CommunityCard>
          <div style={{ padding: "0.45rem 0.55rem", borderRadius: 8, border: "1px dashed #e5e7eb", fontSize: "0.68rem", color: "#6b7280" }}>
            Event · Thu 8:00 PM · speed challenge
          </div>
        </SceneRoot>
      );

    case "account":
      return (
        <SceneRoot>
          <MockClaimRow>
            <MockClaimId>Profile · TG-2026</MockClaimId>
            <MockPill $bg="#dcfce7" $fg="#166534">
              Synced
            </MockPill>
          </MockClaimRow>
          <MockDataRow $ok style={{ marginBottom: 0 }}>
            <MockDataRowLeft>
              <MockRowIcon $ok>✓</MockRowIcon>
              <MockDataLabel>Plan</MockDataLabel>
            </MockDataRowLeft>
            <MockDataValue>Pro · renews Mar</MockDataValue>
          </MockDataRow>
          <MockDataRow $ok style={{ marginBottom: 0 }}>
            <MockDataRowLeft>
              <MockRowIcon $ok>✓</MockRowIcon>
              <MockDataLabel>Devices</MockDataLabel>
            </MockDataRowLeft>
            <MockDataValue>Web + mobile</MockDataValue>
          </MockDataRow>
          <MockCelebrationBox style={{ marginBottom: 0, padding: "0.65rem 0.75rem" }} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <MockCelebrationInner>
              <MockCelebrationIcon aria-hidden>
                <FiCheck strokeWidth={3} size={18} />
              </MockCelebrationIcon>
              <div>
                <MockCelebrationTitle style={{ fontSize: "0.78rem" }}>{workflow.mockCelebrationTitle}</MockCelebrationTitle>
                <MockCelebrationSub style={{ fontSize: "0.64rem" }}>{workflow.mockCelebrationSub}</MockCelebrationSub>
              </div>
            </MockCelebrationInner>
          </MockCelebrationBox>
        </SceneRoot>
      );

    case "podium":
      return (
        <SceneRoot>
          <SceneCaption>This season — climb tiers as you stay consistent.</SceneCaption>
          <PodiumRow>
            <PodiumBlock
              $w="26%"
              $bg="linear-gradient(180deg,#9ca3af,#64748b)"
              $bd="#475569"
              initial={{ height: 0 }}
              animate={{ height: 52 }}
              transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.1 }}
            >
              #2
            </PodiumBlock>
            <PodiumBlock
              $w="32%"
              $bg="linear-gradient(180deg,#fbbf24,#d97706)"
              $bd="#b45309"
              initial={{ height: 0 }}
              animate={{ height: 72 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
            >
              #1
            </PodiumBlock>
            <PodiumBlock
              $w="26%"
              $bg="linear-gradient(180deg,#fdba74,#ea580c)"
              $bd="#c2410c"
              initial={{ height: 0 }}
              animate={{ height: 44 }}
              transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.15 }}
            >
              #3
            </PodiumBlock>
          </PodiumRow>
          <div style={{ textAlign: "center", fontSize: "0.68rem", color: "#64748b", marginTop: 4 }}>
            You · <strong style={{ color: "#0f172a" }}>#184</strong> · Gold tier
          </div>
        </SceneRoot>
      );

    case "dailycard":
      return (
        <SceneRoot>
          <div
            style={{
              borderRadius: 14,
              padding: "0.85rem 1rem",
              background: "linear-gradient(135deg,#faf5ff,#eff6ff)",
              border: "1px solid #e9d5ff",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "0.65rem", fontWeight: 800, color: "#7c3aed", letterSpacing: "0.08em" }}>STREAK</div>
            <div style={{ fontSize: "1.85rem", fontWeight: 900, color: "#1e1b4b", lineHeight: 1.15 }}>
              Day 9 <span aria-hidden>🔥</span>
            </div>
            <div style={{ fontSize: "0.68rem", color: "#64748b", marginTop: 6 }}>Complete today&apos;s passage before midnight</div>
          </div>
          <div
            style={{
              fontSize: "0.72rem",
              fontVariantNumeric: "tabular-nums",
              textAlign: "center",
              color: "#475569",
              fontWeight: 600,
            }}
          >
            Resets in 14:22:01
          </div>
        </SceneRoot>
      );

    case "arcade":
      return (
        <SceneRoot>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div>
              <div style={{ fontSize: "0.62rem", color: "#9ca3af", fontWeight: 700 }}>WORD BLASTER</div>
              <div style={{ fontSize: "0.88rem", fontWeight: 900, color: "#1e1b4b" }}>Wave 3</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "0.62rem", color: "#9ca3af", fontWeight: 700 }}>SCORE</div>
              <div style={{ fontSize: "1rem", fontWeight: 900, color: "#a855f7" }}>12,400</div>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 6,
              padding: "0.5rem",
              background: "#0f172a",
              borderRadius: 10,
              border: "1px solid #334155",
            }}
          >
            {Array.from({ length: 9 }).map((_, i) => (
              <motion.div
                key={`g-${i}`}
                animate={i === 4 ? { opacity: [1, 0.35, 1], scale: [1, 1.08, 1] } : {}}
                transition={{ duration: 1.2, repeat: Infinity }}
                style={{
                  aspectRatio: "1",
                  borderRadius: 6,
                  background: i === 4 ? "#a855f7" : "#1e293b",
                }}
              />
            ))}
          </div>
          <SceneCaption>Quick rounds between lessons — scores sync to your profile.</SceneCaption>
        </SceneRoot>
      );

    case "badgegrid":
      return (
        <SceneRoot>
          <SceneCaption>New drops appear here as you hit real milestones.</SceneCaption>
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", padding: "0.35rem 0" }}>
            {[
              { emoji: "🏆", label: "Gold tier" },
              { emoji: "⭐", label: "7-day streak" },
              { emoji: "🎯", label: "Exam prep" },
            ].map((b) => (
              <div
                key={b.label}
                style={{
                  flex: 1,
                  maxWidth: 88,
                  textAlign: "center",
                  padding: "0.5rem 0.35rem",
                  borderRadius: 12,
                  border: "1px solid #e9d5ff",
                  background: "linear-gradient(180deg,#faf5ff,#fff)",
                }}
              >
                <div style={{ fontSize: "1.35rem" }} aria-hidden>
                  {b.emoji}
                </div>
                <div style={{ fontSize: "0.58rem", fontWeight: 700, color: "#6b21a8", marginTop: 4 }}>{b.label}</div>
              </div>
            ))}
          </div>
          <MockCelebrationBox style={{ marginBottom: 0, padding: "0.65rem 0.75rem" }} initial={{ scale: 0.95 }} animate={{ scale: 1 }}>
            <MockCelebrationInner>
              <MockCelebrationIcon aria-hidden>
                <FiCheck strokeWidth={3} size={18} />
              </MockCelebrationIcon>
              <div>
                <MockCelebrationTitle style={{ fontSize: "0.78rem" }}>{workflow.mockCelebrationTitle}</MockCelebrationTitle>
                <MockCelebrationSub style={{ fontSize: "0.64rem" }}>{workflow.mockCelebrationSub}</MockCelebrationSub>
              </div>
            </MockCelebrationInner>
          </MockCelebrationBox>
        </SceneRoot>
      );

    case "helpsearch":
      return (
        <SceneRoot>
          <SearchFake>
            <FiSearch size={16} color="#9ca3af" aria-hidden />
            <span>How do I reset course progress?</span>
          </SearchFake>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {["Reset progress · courses & lessons", "Billing & subscriptions FAQ", "Keyboard layouts & shortcuts"].map((t) => (
              <div
                key={t}
                style={{
                  padding: "0.45rem 0.55rem",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  fontSize: "0.7rem",
                  color: "#2563eb",
                  fontWeight: 600,
                  cursor: "default",
                }}
              >
                {t}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.65rem", color: "#9ca3af" }}>
            <FiSend size={13} aria-hidden />
            Still stuck? Contact opens from Help.
          </div>
        </SceneRoot>
      );

    default:
      return null;
  }
}

function WorkflowMockPanel({ workflow }) {
  const pct = workflow.mockBarPct ?? 0;
  const gradient = workflow.mockBarGradient || "linear-gradient(90deg, #a855f7, #3b82f6)";
  const rows = workflow.mockRows || [];
  const wfHash = hashString(workflow.id);
  const panel = panelPresetForWorkflowId(workflow.id);
  const rowLine = buildRowVariants(wfHash);
  const rowContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: ROW_STAGGER_PRESETS[wfHash % ROW_STAGGER_PRESETS.length],
    },
  };
  const barTrans = barTransitionForTab(wfHash);
  const pulseDuration = wfHash % 2 === 0 ? 2.6 : 2.1;

  const progressBlock = (
    <div style={{ marginTop: "auto", paddingTop: "0.65rem" }}>
      <MockBarLabelRow>
        <MockBarLabel>{workflow.mockBarLabel || "Progress"}</MockBarLabel>
        <motion.span
          key={`pct-${workflow.id}`}
          initial={{ opacity: 0, rotate: -8, scale: 0.5 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          transition={
            wfHash % 3 === 0
              ? { type: "spring", stiffness: 400, damping: 22, delay: 0.55 }
              : { delay: 0.65, duration: 0.3 }
          }
        >
          <MockBarValue style={{ color: pct >= 85 ? "#059669" : "#0f172a" }}>{pct}%</MockBarValue>
        </motion.span>
      </MockBarLabelRow>
      <MockBarTrack>
        <motion.div
          key={`bar-${workflow.id}`}
          initial={{ width: "0%" }}
          animate={
            wfHash % 2 === 0
              ? {
                  width: `${pct}%`,
                  opacity: [1, 0.86, 1],
                }
              : {
                  width: `${pct}%`,
                  boxShadow: [
                    "0 0 8px rgba(168, 85, 247, 0.22)",
                    "0 0 20px rgba(59, 130, 246, 0.45)",
                    "0 0 8px rgba(168, 85, 247, 0.22)",
                  ],
                }
          }
          transition={{
            width: barTrans,
            opacity:
              wfHash % 2 === 0
                ? { duration: pulseDuration, repeat: Infinity, ease: "easeInOut", delay: 1 }
                : undefined,
            boxShadow:
              wfHash % 2 !== 0
                ? { duration: pulseDuration, repeat: Infinity, ease: "easeInOut", delay: 1 }
                : undefined,
          }}
          style={{
            height: "100%",
            borderRadius: 999,
            background: gradient,
            boxShadow: wfHash % 2 === 0 ? "0 0 14px rgba(168, 85, 247, 0.2)" : undefined,
          }}
        />
      </MockBarTrack>
    </div>
  );

  const userMotion =
    wfHash % 4 === 0
      ? { initial: { opacity: 0, x: -16 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.4 } }
      : wfHash % 4 === 1
        ? { initial: { opacity: 0, scale: 0.85 }, animate: { opacity: 1, scale: 1 }, transition: { type: "spring", stiffness: 380, damping: 20 } }
        : wfHash % 4 === 2
          ? { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.35 } }
          : { initial: { opacity: 0, rotate: -4 }, animate: { opacity: 1, rotate: 0 }, transition: { type: "spring", stiffness: 300, damping: 18 } };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={workflow.id}
        initial={panel.initial}
        animate={panel.animate}
        exit={panel.exit}
        transition={panel.transition}
        style={{ width: "100%", perspective: 900 }}
      >
        <MockShell $mint={workflow.mockMint}>
          <MockWindow>
            <MockChrome>
              <MockDots>
                <MockDot $c="#fb7185" />
                <MockDot $c="#fbbf24" />
                <MockDot $c="#4ade80" />
              </MockDots>
              <MockChromeTitle>{workflow.mockTitle}</MockChromeTitle>
            </MockChrome>
            <MockBody style={{ display: "flex", flexDirection: "column" }}>
              {workflow.mockScene ? (
                <>
                  <WorkflowMockScene workflow={workflow} />
                  {progressBlock}
                </>
              ) : (
                <>
              {workflow.mockKind === "celebration" && (
                <>
                  <motion.div
                    initial={{ opacity: 0, x: wfHash % 2 === 0 ? -12 : 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35 }}
                  >
                    <MockClaimRow>
                      <MockClaimId>Account · TG-2026-{String((wfHash % 90) + 1).padStart(2, "0")}</MockClaimId>
                      <MockPill $bg="#dcfce7" $fg="#166534">
                        Synced
                      </MockPill>
                    </MockClaimRow>
                  </motion.div>
                  <MockCelebrationBox
                    initial={{ opacity: 0, scale: 0.9, y: 14 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={
                      wfHash % 2 === 0
                        ? { type: "spring", stiffness: 320, damping: 22 }
                        : { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
                    }
                  >
                    <MockCelebrationInner>
                      <MockCelebrationIcon aria-hidden>
                        <FiCheck strokeWidth={3} size={20} />
                      </MockCelebrationIcon>
                      <div>
                        <MockCelebrationTitle>{workflow.mockCelebrationTitle}</MockCelebrationTitle>
                        <MockCelebrationSub>{workflow.mockCelebrationSub}</MockCelebrationSub>
                      </div>
                    </MockCelebrationInner>
                  </MockCelebrationBox>
                  {progressBlock}
                </>
              )}

              {workflow.mockKind === "stat" && (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 20, rotateX: 12 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    <MockStatCard>
                      <MockStatCardTitle>{workflow.mockStatTitle}</MockStatCardTitle>
                      <MockStatCardSub>{workflow.mockStatSub}</MockStatCardSub>
                    </MockStatCard>
                  </motion.div>
                  <motion.div variants={rowContainer} initial="hidden" animate="show" style={{ flex: 1 }}>
                    {rows.map((row) => (
                      <MockDataRow
                        key={`${workflow.id}-${row.label}`}
                        variants={rowLine}
                        $ok={row.ok}
                      >
                        <MockDataRowLeft>
                          <MockRowIcon $ok={row.ok}>{row.ok ? "✓" : ""}</MockRowIcon>
                          <MockDataLabel>{row.label}</MockDataLabel>
                        </MockDataRowLeft>
                        <MockDataValue>{row.value}</MockDataValue>
                      </MockDataRow>
                    ))}
                  </motion.div>
                  {progressBlock}
                </>
              )}

              {workflow.mockKind === "checks" && workflow.mockUser && (
                <>
                  <MockUserBlock {...userMotion}>
                    <MockAvatar>{workflow.mockUser.initials}</MockAvatar>
                    <MockUserText>
                      <MockUserName>{workflow.mockUser.name}</MockUserName>
                      <MockUserSub>{workflow.mockUser.sub}</MockUserSub>
                    </MockUserText>
                  </MockUserBlock>
                  <motion.div variants={rowContainer} initial="hidden" animate="show" style={{ flex: 1 }}>
                    {rows.map((row) => (
                      <MockDataRow key={`${workflow.id}-${row.label}`} variants={rowLine} $ok={row.ok}>
                        <MockDataRowLeft>
                          <MockRowIcon $ok={row.ok}>{row.ok ? "✓" : ""}</MockRowIcon>
                          <MockDataLabel>{row.label}</MockDataLabel>
                        </MockDataRowLeft>
                        <MockDataValue>{row.value}</MockDataValue>
                      </MockDataRow>
                    ))}
                  </motion.div>
                  {progressBlock}
                </>
              )}
                </>
              )}
            </MockBody>
          </MockWindow>
        </MockShell>
      </motion.div>
    </AnimatePresence>
  );
}

const leftTextVariants = [
  { initial: { opacity: 0, x: -24 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 20 }, transition: { duration: 0.32 } },
  { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 }, transition: { type: "spring", stiffness: 300, damping: 28 } },
  { initial: { opacity: 0, scale: 0.96 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 1.02 }, transition: { duration: 0.35 } },
  { initial: { opacity: 0, filter: "blur(8px)" }, animate: { opacity: 1, filter: "blur(0px)" }, exit: { opacity: 0, filter: "blur(6px)" }, transition: { duration: 0.4 } },
  { initial: { opacity: 0, rotate: -2 }, animate: { opacity: 1, rotate: 0 }, exit: { opacity: 0, rotate: 2 }, transition: { duration: 0.33 } },
  { initial: { opacity: 0, clipPath: "inset(0 0 100% 0)" }, animate: { opacity: 1, clipPath: "inset(0 0 0% 0)" }, exit: { opacity: 0, clipPath: "inset(0 0 100% 0)" }, transition: { duration: 0.45 } },
];

/** Auto-advance interval (ms). Strip scroll eases active tab from viewport center toward the right as index grows. */
const WORKFLOW_TAB_AUTO_MS = 4200;

/**
 * Marketing strip for the main home page — not Steno-specific.
 */
const HomeWorkflowShowcase = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [autoPaused, setAutoPaused] = useState(false);
  const stripRef = useRef(null);
  const tabBtnRefs = useRef([]);

  const workflow = HOME_WORKFLOW_FEATURES[tab] || HOME_WORKFLOW_FEATURES[0];
  const leftMotion = leftTextVariants[tab % leftTextVariants.length];

  const scrollActiveTabIntoView = useCallback((index) => {
    const container = stripRef.current;
    const btn = tabBtnRefs.current[index];
    if (!container || !btn) return;
    const total = HOME_WORKFLOW_FEATURES.length;
    const maxIdx = Math.max(0, total - 1);
    const midIdx = Math.floor(maxIdx / 2);
    let anchor;
    if (maxIdx === 0) {
      anchor = 0.5;
    } else if (index <= midIdx) {
      // First half of tabs: focal moves from left edge → center (left tab … middle tab)
      const p = midIdx > 0 ? index / midIdx : 0;
      anchor = 0.18 + p * 0.32;
    } else {
      // From middle tab onward to the right: focal moves center → right
      const span = maxIdx - midIdx;
      const p = span > 0 ? (index - midIdx) / span : 0;
      anchor = 0.5 + p * 0.34;
    }
    const btnCenter = btn.offsetLeft + btn.offsetWidth / 2;
    let targetLeft = btnCenter - container.clientWidth * anchor;
    const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
    targetLeft = Math.max(0, Math.min(maxScroll, targetLeft));
    container.scrollTo({ left: targetLeft, behavior: "smooth" });
  }, []);

  useLayoutEffect(() => {
    scrollActiveTabIntoView(tab);
  }, [tab, scrollActiveTabIntoView]);

  useEffect(() => {
    if (autoPaused) return undefined;
    const id = window.setInterval(() => {
      setTab((t) => (t + 1) % HOME_WORKFLOW_FEATURES.length);
    }, WORKFLOW_TAB_AUTO_MS);
    return () => window.clearInterval(id);
  }, [autoPaused]);

  return (
    <ShowcaseSection aria-labelledby="home-workflow-heading">
      <ShowcaseInner>
        <FeatureShowcaseTitle id="home-workflow-heading">
          Streamline your <ExploreCoursesAccent>typing journey</ExploreCoursesAccent>
        </FeatureShowcaseTitle>
        <FeatureShowcaseSub>
          Courses, games, daily challenges, leaderboards, achievements, lessons, practice, races, steno, chat, community,
          help, and more—one connected experience on Typogram.
        </FeatureShowcaseSub>

        <PillStripOuter onMouseEnter={() => setAutoPaused(true)} onMouseLeave={() => setAutoPaused(false)}>
          <PillStripFadeLeft />
          <PillStripFadeRight />
          <PillStripScroll ref={stripRef} role="tablist" aria-label="Product areas">
            {HOME_WORKFLOW_FEATURES.map((f, i) => (
              <PillStripBtn
                key={f.id}
                ref={(el) => {
                  tabBtnRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                aria-selected={tab === i}
                $active={tab === i}
                onClick={() => setTab(i)}
              >
                {f.label}
              </PillStripBtn>
            ))}
          </PillStripScroll>
        </PillStripOuter>

        <FeatureDetailGrid>
          <AnimatePresence mode="wait">
            <motion.div
              key={workflow.id}
              initial={leftMotion.initial}
              animate={leftMotion.animate}
              exit={leftMotion.exit}
              transition={leftMotion.transition}
              style={{ minWidth: 0 }}
            >
              <FeatureDetailHeadingMobile>{workflow.title}</FeatureDetailHeadingMobile>
              <FeatureDetailHeading>{workflow.title}</FeatureDetailHeading>
              <FeatureDetailLead>{workflow.subtitle}</FeatureDetailLead>
              <FeatureBulletList>
                {workflow.bullets.map((line, bulletIdx) => (
                  <FeatureBulletItem
                    key={line}
                    $step={workflow.id === "lesson" ? bulletIdx + 1 : null}
                  >
                    {workflow.id !== "lesson" && <FiCheck strokeWidth={2.5} aria-hidden />}
                    <span>{line}</span>
                  </FeatureBulletItem>
                ))}
              </FeatureBulletList>
              <FeatureCtaBtn type="button" onClick={() => navigate(workflow.ctaPath || "/courses")}>
                Learn more
              </FeatureCtaBtn>
            </motion.div>
          </AnimatePresence>

          <WorkflowMockPanel workflow={workflow} />
        </FeatureDetailGrid>
      </ShowcaseInner>
    </ShowcaseSection>
  );
};

export default HomeWorkflowShowcase;
